import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { HUBS, HUB_MAP, CATEGORIES } from "../../data/hubs";

const STATUS_OPTS = ["draft", "scheduled", "published"];

const fmt = (iso) => { try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" }); } catch { return iso; } };

export const ArticlesAdmin = ({ adminKey, onUnauthorized }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/blog", { headers: { "X-TAG-Admin-Key": adminKey }, validateStatus: () => true });
      if (res.status === 401) { onUnauthorized(); return; }
      setPosts(res.data || []);
    } finally { setLoading(false); }
  }, [adminKey, onUnauthorized]);
  useEffect(() => { load(); }, [load]);

  const startNew = () => { setEditing(null); setShowForm(true); };
  const startEdit = (p) => { setEditing(p); setShowForm(true); };
  const close = () => { setShowForm(false); setEditing(null); load(); };

  const remove = async (slug) => {
    if (!confirm(`Delete ${slug}?`)) return;
    const res = await api.delete(`/blog/${slug}`, { headers: { "X-TAG-Admin-Key": adminKey }, validateStatus: () => true });
    if (res.status === 200) { toast.success("Deleted"); load(); }
    else toast.error("Delete failed");
  };

  const counts = {
    total: posts.length,
    published: posts.filter((p) => p.status === "published").length,
    draft: posts.filter((p) => p.status === "draft").length,
    scheduled: posts.filter((p) => p.status === "scheduled").length,
    views: posts.reduce((s, p) => s + (p.view_count || 0), 0),
  };

  const topByViews = [...posts].sort((a, b) => (b.view_count || 0) - (a.view_count || 0)).slice(0, 5);
  const topByCtas = [...posts].sort((a, b) => {
    const sum = (x) => Object.values(x.cta_clicks || {}).reduce((s, n) => s + n, 0);
    return sum(b) - sum(a);
  }).slice(0, 5);

  return (
    <div data-testid="articles-admin">
      {/* SEO Dashboard KPIs */}
      <div className="kpi-grid" style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 8, marginBottom: 22 }}>
        <Kpi label="Total" value={counts.total} />
        <Kpi label="Published" value={counts.published} color="#3aab5a" />
        <Kpi label="Scheduled" value={counts.scheduled} color="#ba7a30" />
        <Kpi label="Draft" value={counts.draft} color="#888" />
        <Kpi label="Total Views" value={counts.views} color="#FF6575" />
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexWrap: "wrap", gap: 10 }}>
        <h2 style={{ color: "#fff", fontSize: 16, fontWeight: 700, margin: 0 }}>Articles</h2>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={load} data-testid="articles-refresh" className="btn-ghost">↻ Refresh</button>
          <button onClick={startNew} data-testid="articles-new" className="btn-primary">+ New article</button>
        </div>
      </div>

      {showForm && (
        <ArticleForm
          adminKey={adminKey}
          initial={editing}
          onClose={close}
          onUnauthorized={onUnauthorized}
        />
      )}

      {loading && <div className="tag-card" style={{ textAlign: "center", color: "#777" }}>Loading…</div>}
      {!loading && posts.length === 0 && <div className="tag-card" style={{ textAlign: "center", color: "#bbb" }}>No articles yet.</div>}
      {!loading && posts.length > 0 && (
        <div className="tag-card" style={{ padding: 0, overflow: "hidden", marginBottom: 24 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, color: "#bbb" }}>
            <thead>
              <tr style={{ background: "#0d0d0d", borderBottom: "1px solid #1a1a1a" }}>
                {["Title", "Hub", "Category", "Status", "Views", "CTAs", "Created", ""].map((h) => (
                  <th key={h} style={{ textAlign: "left", padding: "12px 14px", color: "#888", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {posts.map((p) => {
                const ctaSum = Object.values(p.cta_clicks || {}).reduce((s, n) => s + n, 0);
                const hub = HUB_MAP[p.hub];
                return (
                  <tr key={p.slug} data-testid={`article-row-${p.slug}`} style={{ borderBottom: "1px solid #161616" }}>
                    <td style={{ padding: "10px 14px", color: "#fff", maxWidth: 380 }}>{p.title}</td>
                    <td style={{ padding: "10px 14px", color: hub?.accent || "#777" }}>{hub?.name || "—"}</td>
                    <td style={{ padding: "10px 14px" }}>{p.category}</td>
                    <td style={{ padding: "10px 14px" }}>
                      <span style={{
                        fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em",
                        padding: "3px 8px", borderRadius: 20,
                        background: p.status === "published" ? "#0a1a0a" : p.status === "scheduled" ? "#1a100a" : "#141414",
                        color: p.status === "published" ? "#3aab5a" : p.status === "scheduled" ? "#ba7a30" : "#888",
                      }}>{p.status || "published"}</span>
                    </td>
                    <td style={{ padding: "10px 14px", color: "#fff" }}>{p.view_count || 0}</td>
                    <td style={{ padding: "10px 14px", color: "#FF6575" }}>{ctaSum}</td>
                    <td style={{ padding: "10px 14px", color: "#777" }}>{fmt(p.created_at)}</td>
                    <td style={{ padding: "10px 14px", whiteSpace: "nowrap" }}>
                      <button onClick={() => startEdit(p)} className="btn-ghost" style={{ padding: "4px 10px", fontSize: 11 }}>Edit</button>{" "}
                      <button onClick={() => remove(p.slug)} className="btn-ghost" style={{ padding: "4px 10px", fontSize: 11 }}>Del</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }} className="kpi-grid">
        <TopList title="Top viewed articles" rows={topByViews} field={(p) => p.view_count || 0} />
        <TopList title="Most-clicked CTAs" rows={topByCtas} field={(p) => Object.values(p.cta_clicks || {}).reduce((s, n) => s + n, 0)} />
      </div>
    </div>
  );
};

const TopList = ({ title, rows, field }) => (
  <div className="tag-card">
    <h3 style={{ color: "#fff", fontSize: 14, fontWeight: 700, margin: "0 0 12px" }}>{title}</h3>
    {rows.length === 0 && <div style={{ color: "#777", fontSize: 12 }}>No data yet.</div>}
    {rows.map((p) => (
      <div key={p.slug} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid #1a1a1a", fontSize: 12 }}>
        <span style={{ color: "#bbb", paddingRight: 12 }}>{p.title}</span>
        <span style={{ color: "#FF6575", fontWeight: 700 }}>{field(p)}</span>
      </div>
    ))}
  </div>
);

const Kpi = ({ label, value, color = "#fff" }) => (
  <div className="tag-card" style={{ padding: "12px 14px" }}>
    <div style={{ color, fontSize: 22, fontWeight: 700, lineHeight: 1.1 }}>{value}</div>
    <div style={{ color: "#888", fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", marginTop: 4 }}>{label}</div>
  </div>
);

const ArticleForm = ({ adminKey, initial, onClose, onUnauthorized }) => {
  const isEdit = !!initial;
  const [form, setForm] = useState(initial ? { ...initial, tags: (initial.tags || []).join(", "), faq: JSON.stringify(initial.faq || [], null, 2) } : {
    title: "", slug: "", category: CATEGORIES[0], hub: HUBS[0].slug,
    excerpt: "", body: "", tags: "",
    seo_title: "", meta_description: "", featured_image: "",
    faq: "[]", status: "draft",
  });
  const [submitting, setSubmitting] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.slug.trim() || !form.body.trim()) return toast.error("Title, slug, and body required.");
    let faq;
    try { faq = JSON.parse(form.faq || "[]"); } catch { return toast.error("FAQ must be valid JSON (array of {q,a})."); }
    setSubmitting(true);
    const payload = {
      ...form,
      tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      faq,
    };
    const res = isEdit
      ? await api.patch(`/blog/${initial.slug}`, payload, { headers: { "X-TAG-Admin-Key": adminKey }, validateStatus: () => true })
      : await api.post("/blog", payload, { headers: { "X-TAG-Admin-Key": adminKey }, validateStatus: () => true });
    setSubmitting(false);
    if (res.status === 401) { onUnauthorized(); return; }
    if (res.status >= 200 && res.status < 300) { toast.success(isEdit ? "Updated" : "Created"); onClose(); }
    else toast.error(res.data?.detail || "Save failed");
  };

  return (
    <div className="tag-card" data-testid="article-form" style={{ marginBottom: 22 }}>
      <h2 style={{ color: "#fff", fontSize: 16, fontWeight: 700, margin: "0 0 16px" }}>{isEdit ? "Edit article" : "New article"}</h2>
      <form onSubmit={submit}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <Field label="Title"><input data-testid="af-title" className="tag-input" value={form.title} onChange={(e) => set("title", e.target.value)} /></Field>
          <Field label="Slug"><input data-testid="af-slug" className="tag-input" value={form.slug} onChange={(e) => set("slug", e.target.value)} disabled={isEdit} /></Field>
          <Field label="Hub">
            <select data-testid="af-hub" className="tag-input" value={form.hub} onChange={(e) => set("hub", e.target.value)}>
              {HUBS.map((h) => <option key={h.slug} value={h.slug}>{h.name}</option>)}
            </select>
          </Field>
          <Field label="Category">
            <select data-testid="af-category" className="tag-input" value={form.category} onChange={(e) => set("category", e.target.value)}>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Status">
            <select data-testid="af-status" className="tag-input" value={form.status} onChange={(e) => set("status", e.target.value)}>
              {STATUS_OPTS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Featured image URL"><input data-testid="af-img" className="tag-input" value={form.featured_image} onChange={(e) => set("featured_image", e.target.value)} placeholder="https://…" /></Field>
        </div>
        <Field label="Excerpt"><textarea data-testid="af-excerpt" rows={2} className="tag-input" value={form.excerpt} onChange={(e) => set("excerpt", e.target.value)} style={{ fontFamily: "var(--font-body)" }} /></Field>
        <Field label="Body (markdown / plain)"><textarea data-testid="af-body" rows={10} className="tag-input" value={form.body} onChange={(e) => set("body", e.target.value)} style={{ fontFamily: "var(--font-body)" }} /></Field>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <Field label="SEO title"><input data-testid="af-seo" className="tag-input" value={form.seo_title} onChange={(e) => set("seo_title", e.target.value)} /></Field>
          <Field label="Meta description"><input data-testid="af-meta" className="tag-input" value={form.meta_description} onChange={(e) => set("meta_description", e.target.value)} /></Field>
        </div>
        <Field label="Tags (comma-separated)"><input data-testid="af-tags" className="tag-input" value={form.tags} onChange={(e) => set("tags", e.target.value)} /></Field>
        <Field label="FAQ (JSON: [{q,a}…])"><textarea data-testid="af-faq" rows={4} className="tag-input" value={form.faq} onChange={(e) => set("faq", e.target.value)} style={{ fontFamily: "monospace", fontSize: 11 }} /></Field>
        <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
          <button data-testid="af-save" type="submit" className="btn-primary" disabled={submitting}>{submitting ? "Saving…" : (isEdit ? "Save changes" : "Create")}</button>
          <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
        </div>
      </form>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div style={{ marginBottom: 12 }}>
    <label style={{ display: "block", color: "#888", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>{label}</label>
    {children}
  </div>
);
